import { useQueryClient } from '@tanstack/react-query';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { ApiService } from '../../services/index';
import { SubscriptionOffer } from '../../types/offer.types';
import { SubscriptionType } from '../../types/plan.types';
import { useIdempotentAppMutation } from '../use-idempotent-app-mutation.hook';

export type UpdateSubscriptionOfferDto = {
  companyDomain: string;
  companyName: string;
  docusignEnvelopeLimit?: null | number;
  isMonthlyEnabled: boolean;
  monthlyPriceInCents?: null | number;
  planType: SubscriptionType;
  promoCode?: null | string;
  recipients: string[];
  stakeholdersLimit: number;
  yearlyPriceInCents?: null | number;
};

// tenant: global — a subscription offer has no owning company until it is claimed and paid, so it is
// addressed by its own id (/subscription-offers/:id) rather than companies/:companyId. Editing an
// incomplete offer keeps the same slug/link; the customer sees the new terms next time they open it.
export const useUpdateSubscriptionOffer = () => {
  const queryClient = useQueryClient();

  const {
    mutate: updateSubscriptionOffer,
    mutateAsync: updateSubscriptionOfferAsync,
    isPending,
  } = useIdempotentAppMutation([QueryKey.UPDATE_SUBSCRIPTION_OFFER], {
    mutationFn: ({ id, data }: { data: UpdateSubscriptionOfferDto; id: string }, { headers }) =>
      ApiService.patch<SubscriptionOffer, UpdateSubscriptionOfferDto>(
        { endpoint: BackendRoute.SUBSCRIPTION_OFFERS, routePath: [id] },
        { body: data, headers },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.GET_SUBSCRIPTION_OFFERS] });
    },
  });

  return { updateSubscriptionOffer, updateSubscriptionOfferAsync, isPending };
};
